import { graphql } from 'gatsby'
import React from 'react'

import Anchor from '@/components/anchor/anchor'
import Image from '@/components/image/image'
import Link from '@/components/link/link'
import useDefaultImages from '@/hooks/useDefaultImages'
import { generateUrl } from '@/internal/utils/generate-url'
import { CountObject } from '@/types/datocms-plugin-word-counter'
import { anchorFormatted } from '@/utils/format'

import ImageDirection from '../image-direction'
import * as style from './interviews-section.module.scss'

type InterviewsSectionProps = Queries.InterviewsSectionFragment

const InterviewsSection: React.FC<InterviewsSectionProps> = ({
  title,
  items
}) => {
  const { imgInterviewsSection } = useDefaultImages('DatoCmsPctInterview')
  if (!title && !items) {
    return null
  }
  return (
    <section className={style.section}>
      {title && <Anchor anchor={anchorFormatted(title)} />}
      <div className="container">
        <div className="row">
          <div className="col-12">
            <h2 className={`h300 text-base-000 ${style.title}`}>{title}</h2>
          </div>
        </div>
        <div className="row">
          {items &&
            items?.length > 0 &&
            items.map((item, index) => {
              const author = item?.authors?.[0]

              const name = `${author?.person?.firstName} ${author?.person?.secondName}`

              const date = item?.meta?.firstPublishedAt

              const wordCounter = JSON.parse(
                item?.contentWordCounter as unknown as string
              ) as CountObject

              const link = item?.slug
                ? generateUrl(item?.__typename, item?.slug)
                : ''

              return (
                <div
                  className={`col-12 col-md-6 ${style.itemList}`}
                  key={`interview-${index}`}
                >
                  <div className={style.interview}>
                    <div className={style.interviewHeader}>
                      <ImageDirection
                        alt={item?.image?.alt ?? item?.imageMobile?.alt}
                        image={item?.image ?? imgInterviewsSection?.desktop}
                        imageMobile={
                          item?.imageMobile ?? imgInterviewsSection?.mobile
                        }
                        className={style.interviewHeaderImage}
                      />
                      <div className={style.interviewAuthor}>
                        <div className={style.interviewAuthorImage}>
                          <Image image={author?.person?.photo} />
                        </div>
                      </div>
                    </div>
                    <div className={style.interviewBody}>
                      <div className={style.interviewAuthorInfo}>
                        <p className="b200-bold text-base-000 my-0">{name}</p>
                        <p className="b100 text-base-100 my-0">
                          {author?.person?.job}
                        </p>
                      </div>
                      <div className={style.interviewBodyContent}>
                        <div className={style.interviewWrapperInfo}>
                          <span className="b100 text-base-200">{date}</span>
                          {wordCounter?.readingTime && (
                            <span
                              className={`b100 text-base-200 ${
                                date ? style.interviewWrapperInfoSeparator : ''
                              }`}
                            >
                              {wordCounter.readingTime}
                            </span>
                          )}
                        </div>
                        <h4 className="b200-bold text-base-000">
                          {item?.title}
                        </h4>
                        <p className="b100 text-base-100 my-0">
                          {item?.excerpt}
                        </p>
                      </div>
                      <Link to={link} className={style.interviewCta}>
                        Read more
                      </Link>
                    </div>
                  </div>
                </div>
              )
            })}
        </div>
      </div>
    </section>
  )
}

export default InterviewsSection
export const query = graphql`
  fragment InterviewsSection on DatoCmsInterviewsSection {
    title
    items {
      ... on DatoCmsPctInterview {
        __typename
        image {
          gatsbyImageData(
            imgixParams: { fit: "crop", h: "325", w: "1250", crop: "bottom" }
          )
          alt
        }
        imageMobile: image {
          gatsbyImageData(height: 325)
          alt
        }
        slug
        intro: headline
        title: externalName
        excerpt
        contentWordCounter
        meta {
          firstPublishedAt(formatString: "MMMM do, YYYY", locale: "en")
        }
        authors {
          person {
            firstName
            secondName
            job
            linkedin
            company {
              externalName
              url
            }
            photo {
              alt
              gatsbyImageData(
                width: 400
                height: 400
                imgixParams: { w: "400", h: "400", fit: "crop" }
              )
            }
          }
        }
      }
    }
  }
`
